import { Button, CopyToClipboard, Dropdown, Spinner } from '@components';
import Tooltip from '@components/tooltip/Tooltip';
import useIsTruncated from '@hooks/useIsTruncated';

import { priorityOptions } from '../constants/lead-info';
import { LeadHeaderProps } from '../types/leadDetails';
import { deriveDarkColor } from '../utils/common';
import { HorizontalStepper } from './horizontal-stepper/components/horizontal-stepper';
import LeadTags from './LeadTags';

import { cofeeNeutral1 } from '../../../../themes/colors';
import { LeadTag } from '../../../types/lead-management';

const LeadIdRow = ({ id }: { id: string }) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>(id);

  return (
    <div className="flex items-center gap-1.5">
      <span className="shrink-0 text-sm text-cofeeNeutral8">Lead ID:</span>
      <span
        ref={ref}
        className="max-w-50 truncate text-sm text-cofeeNeutral8"
        data-tooltip-id="lead-id-tooltip"
      >
        {id}
      </span>
      {isTruncated && (
        <Tooltip id="lead-id-tooltip" type="text" tooltipText={id} />
      )}
      <CopyToClipboard textToCopy={id} iconColor="text-cofeePrimary6" />
    </div>
  );
};

const LeadHeader = ({
  leadData,
  isOnboarded,
  selectedPriority,
  isUpdatingPriority,
  isUpdating,
  stepperSteps,
  currentStepIndex,
  statusOptions,
  updatingStepId,
  selectedAssignee,
  isUpdatingAssignee,
  assigneeOptions,
  isAssigneeDisabled = false,
  onPriorityChange,
  onEditInformation,
  onMoveToCofee,
  onStepClick,
  onStatusChange,
  onAddTags,
  onAssigneeChange
}: LeadHeaderProps) => {
  const { ref: nameRef, isTruncated: isNameTruncated } =
    useIsTruncated<HTMLDivElement>(leadData.name);

  return (
    <div className="relative rounded-xl bg-white p-6 shadow-sm">
      {(isUpdating || isUpdatingAssignee || isUpdatingPriority) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white">
          <Spinner size="lg" />
        </div>
      )}
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4 md:items-center md:gap-10">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-3">
                <div
                  ref={nameRef}
                  data-tooltip-id={
                    isNameTruncated ? 'lead-name-tooltip' : undefined
                  }
                  className="line-clamp-2 min-w-0 break-words text-2xl font-semibold text-gray-900"
                >
                  {leadData.name}
                </div>
                {isOnboarded && (
                  <span className="shrink-0 whitespace-nowrap rounded-3xl border border-grey30 px-3 py-1.5 text-sm text-cofeeNeutral8">
                    Moved to CoFee
                  </span>
                )}
              </div>
              {isNameTruncated && (
                <Tooltip
                  id="lead-name-tooltip"
                  place="bottom"
                  className="!max-w-400 !break-all"
                >
                  {leadData.name}
                </Tooltip>
              )}
            </div>
            <LeadIdRow id={leadData.display_lead_id || leadData.id} />
          </div>

          <div className="flex shrink-0 items-center gap-4">
            <Button
              variant="tertiary"
              label="Edit"
              leftIconName="edit-file"
              handleClick={onEditInformation}
              className="h-11 px-4 py-2.5"
              leftIconStyle="!size-5"
              leftIconStroke="#7E7E7E"
            />
            {!isOnboarded && (
              <Button
                variant="primary"
                label="Move to CoFee"
                leftIconName="plus"
                leftIconStroke={cofeeNeutral1}
                handleClick={onMoveToCofee}
                className="h-11 whitespace-nowrap px-3 py-2.5"
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="whitespace-nowrap text-sm font-medium text-gray-600">
            Lead Status:
          </span>

          <HorizontalStepper
            steps={stepperSteps}
            currentStepIndex={currentStepIndex}
            statusOptions={statusOptions.map((status) => ({
              id: status.id,
              label: status.label,
              color: status.color
            }))}
            onStepClick={onStepClick}
            onStatusChange={onStatusChange}
            className="size-full"
            disabled={isUpdating}
            updatingStepId={updatingStepId}
          />
        </div>

        <div className="flex items-start gap-3">
          <span className="mt-2 text-sm font-medium text-gray-600">Tags:</span>
          <div className="flex flex-wrap items-center gap-2">
            {leadData.tags.map((tag: LeadTag) => (
              <div
                key={tag.id}
                className="text-14 rounded-3xl px-3 py-1.5"
                style={{
                  backgroundColor: tag.color || '#e5e7eb',
                  color: deriveDarkColor(tag.color || '#e5e7eb')
                }}
              >
                <span className="text-sm font-normal">{tag?.name}</span>
              </div>
            ))}

            <LeadTags handleAddTagButtonClick={onAddTags} showAddButton />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-600">Assignee:</span>
            <Dropdown
              currentFilter={selectedAssignee}
              filterOptions={assigneeOptions}
              onSelectFilter={onAssigneeChange}
              className="!w-115 !h-fit !min-w-[96px] !rounded-full !border-grey30 !px-3 !py-1.5 focus:!border-[#BCBCBC] focus:!bg-[#FAFAFA]"
              buttonClassName="!bg-white !border-none !text-gray-900 !min-w-[96px] disabled:!bg-transparent"
              optionsStyle="!min-w-[160px]"
              disabled={isUpdatingAssignee || isAssigneeDisabled}
              labelStyle="!text-14  whitespace-nowrap !font-normal !text-[#000000DE]"
              searchBarPlaceholder="Search Assignee"
            />
          </div>

          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-600">Priority:</span>
            <Dropdown
              currentFilter={selectedPriority}
              filterOptions={priorityOptions}
              onSelectFilter={onPriorityChange}
              className="!w-107 !h-fit !min-w-[96px] !rounded-full !border-grey30 !px-3 !py-1.5 focus:!border-[#BCBCBC] focus:!bg-[#FAFAFA]"
              buttonClassName="!bg-white !border-none !text-gray-900 disabled:!bg-transparent"
              optionsStyle="!min-w-[160px]"
              disabled={isUpdatingPriority}
              labelStyle="!text-14  whitespace-nowrap !font-normal !text-[#000000DE]"
              searchBarPlaceholder="Search priority"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadHeader;
